import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  IconButton,
  TextField
} from '@mui/material';
import { ViewModule, AccountBalance, CorporateFare, Edit, Delete, Check, Close, AdminPanelSettings } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  StyledTableRow,
  HeaderCell,
  SaveButton,
  FootnoteContainer,
  FootnoteText,
  StyledCheckbox,
  StyledTableCell
} from './ModulePreferences.styled';
import CustomTabs from './CustomTabs';
import CustomTab from './CustomTab';
// Removed hardcoded PPO_OPTIONS import
import PermissionConstants from '../../../common/constants/permissionConstants';
import { ModuleNames, UserRoles } from '../../../common/constants/userConstants';
import { usePermission } from '../../../hooks/usePermission';
import { useUser } from '../../../hooks/useUser';
import { User } from '../../../types/user';
import { useDesignatedOperatorRelations } from '../../../hooks/useDesignatedOperatorRelations';
import { DesignatedOperatorRelation, Module } from '../../../types/designatedOperatorRelations';
import axios from '../../../common/api/request';
import { useSettingsYears } from '../../../hooks/useSettingsYears';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';

interface ModuleColumn {
  id: string;
  name: string;
}

interface DORow {
  id: number | null;
  originalDO: string;
  targetDO: string;
  year: number;
  modules: Record<string, boolean>;
  moduleEditability: Record<string, boolean>; // Track editable status for each module
}

interface IPCAccountingGroup {
  id: number;
  name: string;
  operator: {
    id: number;
    name: string;
    longName: string | null;
    disabled: boolean;
  };
  displayName: string;
  predefined: boolean;
  homeCountry: boolean;
}

interface UPUAccountingGroup {
  id: number;
  name: string;
  operator: {
    id: number;
    name: string;
    longName: string | null;
    disabled: boolean;
  };
  operatorId: number | null;
  displayName: string;
  predefined: boolean;
  homeCountry: boolean;
}

interface OperatorModule {
  operatorId: string;
  moduleName: string;
  enabled: boolean;
}

interface OperatorModulesData {
  operatorId: string;
  modules: Record<string, boolean>;
}

interface PaginatedOperatorModulesResponse {
  content: OperatorModule[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// Years will be fetched dynamically from the API

const ModulePreferences: React.FC = () => {
  const { t } = useTranslation();

  // State for dynamic years from API (Settings superset, NOT preference-filtered)
  const { years: settingsYears, isLoading: isLoadingYears, error: yearsErrorObj } = useSettingsYears();
  const availableYears = settingsYears;
  const yearsError = yearsErrorObj ? 'Failed to load available years' : null;

  const [selectedYear, setSelectedYear] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [pendingYear, setPendingYear] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number>(0);

  // Use the new hook for designated operator relations
  // Parse the selected year, defaulting to 0 if invalid (hook will handle this gracefully)
  const yearForHook = selectedYear && !isNaN(parseInt(selectedYear)) ? parseInt(selectedYear) : 0;

  // Only fetch DO relations when tab 0 is active AND we have a valid year
  const shouldFetchDORelations = activeTab === 0 && yearForHook > 0;

  const {
    relations: doRelations,
    isLoading: isLoadingRelations,
    error: relationsError,
    saveRelations
  } = useDesignatedOperatorRelations(yearForHook, shouldFetchDORelations);

  // IPC Accounting groups - populated from backend
  const [ipcAccountingGroups, setIpcAccountingGroups] = useState<IPCAccountingGroup[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState<boolean>(false);

  // UPU Accounting groups - populated from backend
  const [upuAccountingGroups, setUpuAccountingGroups] = useState<UPUAccountingGroup[]>([]);
  const [isLoadingUpuGroups, setIsLoadingUpuGroups] = useState<boolean>(false);

  // Inline editing state
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);
  const [editingGroupName, setEditingGroupName] = useState<string>('');
  const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);

  // UPU Inline editing state
  const [editingUpuGroupId, setEditingUpuGroupId] = useState<number | null>(null);
  const [editingUpuGroupName, setEditingUpuGroupName] = useState<string>('');
  const [isSavingUpuEdit, setIsSavingUpuEdit] = useState<boolean>(false);

  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [groupToDelete, setGroupToDelete] = useState<IPCAccountingGroup | null>(null);

  // UPU Delete confirmation modal state
  const [deleteUpuModalOpen, setDeleteUpuModalOpen] = useState<boolean>(false);
  const [upuGroupToDelete, setUpuGroupToDelete] = useState<UPUAccountingGroup | null>(null);

  // Add group modal state
  const [addGroupModalOpen, setAddGroupModalOpen] = useState<boolean>(false);
  const [newGroupName, setNewGroupName] = useState<string>('');
  const [isAddingGroup, setIsAddingGroup] = useState<boolean>(false);

  // UPU Add group modal state
  const [addUpuGroupModalOpen, setAddUpuGroupModalOpen] = useState<boolean>(false);
  const [newUpuGroupName, setNewUpuGroupName] = useState<string>('');
  const [isAddingUpuGroup, setIsAddingUpuGroup] = useState<boolean>(false);

  // Local state for DO relations (for editing)
  const [localDORelations, setLocalDORelations] = useState<DORow[]>([]);

  // Operator Modules state (ADMIN only)
  const [operatorModules, setOperatorModules] = useState<OperatorModulesData[]>([]);
  const [isLoadingOperatorModules, setIsLoadingOperatorModules] = useState<boolean>(false);
  const [hasUnsavedOperatorChanges, setHasUnsavedOperatorChanges] = useState<boolean>(false);

  // Notification state for operator modules
  const [operatorModulesNotification, setOperatorModulesNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Fixed operator module columns with mapping to backend module names
  const operatorModuleColumnMapping: Record<string, string> = {
    'eNO1': 'ENO1',
    'IPC Accounting': 'TDACC'
  };
  
  // Fixed columns for operator modules
  const fixedOperatorModuleColumns: ModuleColumn[] = [
    { id: 'eNO1', name: 'eNO1' },
    { id: 'IPC Accounting', name: 'IPC Accounting' }
  ];
  
  const [operatorModuleColumns] = useState<ModuleColumn[]>(fixedOperatorModuleColumns);

  // Helper function to map backend module name to display name
  const mapBackendToDisplayName = (backendName: string): string | null => {
    const normalizedBackendName = backendName.toUpperCase();
    // Find the display name that maps to this backend name
    for (const [displayName, backendMapping] of Object.entries(operatorModuleColumnMapping)) {
      if (backendMapping.toUpperCase() === normalizedBackendName) {
        return displayName;
      }
    }
    return null;
  };

  // Get current user information
  const { data: userInfo, isLoading: isLoadingUser } = useUser();
  const user = userInfo as unknown as User | undefined;

  // Check if user is an admin or super user
  const isAdminOrSuperUser = user?.role === UserRoles.ADMIN || user?.role === UserRoles.SUPER_USER;

  // Check if user is an admin (for operator modules tab)
  // Check admin status when user data exists and is loaded
  const isAdmin = !isLoadingUser && !!userInfo && user?.role === UserRoles.ADMIN;

  // Permission checks for each module using permissions from PermissionConstants
  const { hasPermission: originalEno1Access } = usePermission(
    "/eno1",
    "/samples"
  );

  const { hasPermission: originalE53Access } = usePermission(
    "/e53",
    "/samples"
  );

  const { hasPermission: originalE55Access } = usePermission(
    '/e55',
    '/despatches'
  );

  const { hasPermission: originalE09Access } = usePermission(
    '/e09',
    '/despatches'
  );

  // Override permissions if user is admin or super user
  const hasEno1Access = isAdminOrSuperUser ? true : originalEno1Access;
  const hasE53Access = isAdminOrSuperUser ? true : originalE53Access;
  const hasE55Access = isAdminOrSuperUser ? true : originalE55Access;
  const hasE09Access = isAdminOrSuperUser ? true : originalE09Access;

  // Dynamic module columns based on API response
  const [moduleColumns, setModuleColumns] = useState<ModuleColumn[]>([]);

  // Initialize selectedYear when settings years are loaded
  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears]);

  // Transform DO relations to local state for editing and extract module columns
  useEffect(() => {
    // Only process when tab 0 is active
    if (activeTab !== 0) {
      setModuleColumns([]);
      setLocalDORelations([]);
      return;
    }

    if (doRelations && doRelations.length > 0) {
      // Extract unique modules from the first relation to build module columns
      const firstRelation = doRelations[0];
      const uniqueModules = firstRelation.modules.map(module => ({
        id: module.name.toLowerCase(),
        name: module.name
      }));
      setModuleColumns(uniqueModules);

      const transformedRelations: DORow[] = doRelations.map(relation => {
        const modules: Record<string, boolean> = {};
        const moduleEditability: Record<string, boolean> = {};
        relation.modules.forEach(module => {
          // Use the module name as-is since it's already lowercase from API
          modules[module.name] = module.enabled;
          // Store editable status (default to true if not provided)
          moduleEditability[module.name] = module.editable !== false;
        });

        return {
          id: relation.id || 0, // Handle null IDs from API
          originalDO: relation.originalDO,
          targetDO: relation.targetDO,
          year: relation.year,
          modules,
          moduleEditability
        };
      });

      setLocalDORelations(transformedRelations);
    } else {
      // If no relations exist, show empty state
      setModuleColumns([]);
      setLocalDORelations([]);
    }
  }, [doRelations, selectedYear, activeTab]);

  // Fetch IPC Accounting groups from backend - only when tab 1 is active
  useEffect(() => {
    const fetchIpcAccountingGroups = async () => {
      if (activeTab !== 1) {
        setIpcAccountingGroups([]);
        setIsLoadingGroups(false);
        return;
      }

      setIsLoadingGroups(true);
      try {
        const response = await axios.get('/rg/rgs-for-operator');
        setIpcAccountingGroups(response.data);
      } catch (error) {
        console.error('Failed to fetch IPC accounting groups:', error);
        // Keep empty array on error
        setIpcAccountingGroups([]);
      } finally {
        setIsLoadingGroups(false);
      }
    };

    fetchIpcAccountingGroups();
  }, [activeTab]);

  // Fetch UPU Accounting groups from backend - only when tab 2 is active
  useEffect(() => {
    const fetchUpuAccountingGroups = async () => {
      if (activeTab !== 2) {
        setUpuAccountingGroups([]);
        setIsLoadingUpuGroups(false);
        return;
      }

      setIsLoadingUpuGroups(true);
      try {
        const response = await axios.get('/upu-rg/rgs-for-operator');
        setUpuAccountingGroups(response.data);
      } catch (error) {
        console.error('Failed to fetch UPU accounting groups:', error);
        // Keep empty array on error
        setUpuAccountingGroups([]);
      } finally {
        setIsLoadingUpuGroups(false);
      }
    };

    fetchUpuAccountingGroups();
  }, [activeTab]);

  // Fetch Operator Modules from backend (ADMIN only) - only when tab 3 is active
  useEffect(() => {
    const fetchOperatorModules = async () => {
      // Don't fetch if user data is loading, doesn't exist, user is not admin, or tab 3 is not active
      if (isLoadingUser || !userInfo || !isAdmin || activeTab !== 3) {
        setOperatorModules([]);
        setIsLoadingOperatorModules(false);
        return;
      }

      setIsLoadingOperatorModules(true);
      try {
        // Fetch all pages of operator modules
        let allModules: OperatorModule[] = [];
        let currentPage = 0;
        let totalPages = 1;

        do {
          const response = await axios.get<PaginatedOperatorModulesResponse>('/operator-modules', {
            params: {
              page: currentPage,
              size: 100 // Fetch 100 per page
            }
          });

          allModules = [...allModules, ...response.data.content];
          totalPages = response.data.totalPages;
          currentPage++;
        } while (currentPage < totalPages);

        // Get unique operator IDs from modules only (no need to fetch operators separately)
        const operatorIdsFromModules = [...new Set(allModules.map(m => m.operatorId))];
        const allOperatorIds = operatorIdsFromModules;

        // Transform API response to grouped structure
        const grouped: Record<string, OperatorModulesData> = {};

        // Initialize all operators with empty modules
        allOperatorIds.forEach(operatorId => {
          grouped[operatorId] = {
            operatorId,
            modules: {}
          };
        });

        // Populate modules from API response, mapping backend names to display names
        allModules.forEach((item) => {
          if (!grouped[item.operatorId]) {
            grouped[item.operatorId] = {
              operatorId: item.operatorId,
              modules: {}
            };
          }
          // Map backend module name to display name
          const displayName = mapBackendToDisplayName(item.moduleName);
          if (displayName) {
            grouped[item.operatorId].modules[displayName] = item.enabled;
          }
        });

        // Initialize missing modules as false for all operators (using fixed columns)
        Object.keys(grouped).forEach(operatorId => {
          fixedOperatorModuleColumns.forEach(column => {
            if (!grouped[operatorId].modules.hasOwnProperty(column.id)) {
              grouped[operatorId].modules[column.id] = false;
            }
          });
        });

        // Sort operators alphabetically
        const sortedOperators = Object.values(grouped).sort((a, b) =>
          a.operatorId.localeCompare(b.operatorId)
        );

        setOperatorModules(sortedOperators);
        setHasUnsavedOperatorChanges(false);
      } catch (error) {
        console.error('Failed to fetch operator modules:', error);
        setOperatorModules([]);
      } finally {
        setIsLoadingOperatorModules(false);
      }
    };

    fetchOperatorModules();
  }, [isAdmin, activeTab, userInfo, isLoadingUser]);


  // Handle checkbox change for DO relations
  const handleDORelationCheckboxChange = (targetDO: string, moduleId: string) => {
    setLocalDORelations(prevRelations =>
      prevRelations.map(relation =>
        relation.targetDO === targetDO
          ? { ...relation, modules: { ...relation.modules, [moduleId]: !relation.modules[moduleId] } }
          : relation
      )
    );
    setHasUnsavedChanges(true);
  };

  // Check if any module in a relation has editable: false
  const hasNonEditableModule = (relation: DORow): boolean => {
    return Object.values(relation.moduleEditability).some(editable => editable === false);
  };

  // Check if a specific module checkbox should be disabled
  const isModuleCheckboxDisabled = (relation: DORow, moduleId: string): boolean => {
    const isEditable = relation.moduleEditability[moduleId] !== false;
    const hasPermission = getModulePermission(moduleId);
    return !isEditable || !hasPermission;
  };


  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPendingYear(null);
  };

  // Handle confirming year change despite unsaved changes
  const handleConfirmYearChange = () => {
    if (pendingYear) {
      setSelectedYear(pendingYear);
      setHasUnsavedChanges(false);
      setOpenDialog(false);
      setPendingYear(null);
    }
  };

  const handleYearChange = (event: SelectChangeEvent) => {
    const newYear = event.target.value as string;

    // Check if there are unsaved changes
    if (hasUnsavedChanges) {
      // Store the pending year and show confirmation dialog
      setPendingYear(newYear);
      setOpenDialog(true);
    } else {
      // No unsaved changes, directly change the year
      setSelectedYear(newYear);
    }
  };

  // Check if user has access to at least one module
  const hasModuleAccess = hasEno1Access || hasE53Access || hasE55Access || hasE09Access;

  // Dynamic module permission mapping
  const getModulePermission = (moduleId: string): boolean => {
    switch (moduleId.toLowerCase()) {
      case 'eno1':
        return hasEno1Access;
      case 'e53':
        return hasE53Access;
      case 'e55':
        return hasE55Access;
      case 'e09':
        return hasE09Access;
      default:
        return false;
    }
  };

  const handleSave = async () => {
    if (!hasModuleAccess) {
      console.warn('User does not have permission to save module preferences');
      return;
    }

    try {
      // Transform local DO relations back to API format
      const relationsToSave: DesignatedOperatorRelation[] = localDORelations.map(relation => {
        // Find the original relation to get the correct module IDs
        const originalRelation = doRelations.find(orig => orig.targetDO === relation.targetDO);

        return {
          id: relation.id || null, // Preserve null for new relations
          originalDO: relation.originalDO,
          targetDO: relation.targetDO,
          year: relation.year,
          modules: moduleColumns.map(column => {
            // Find the original module to get its ID
            const originalModule = originalRelation?.modules.find(mod => mod.name.toLowerCase() === column.id);

            return {
              id: originalModule?.id || null, // Use original ID or null for new modules
              name: column.name.toLowerCase(), // Ensure module names are lowercase
              enabled: relation.modules[column.id] && getModulePermission(column.id),
              editable: relation.moduleEditability[column.id] !== false ? undefined : false // Preserve editable status
            };
          })
        };
      });

      // Debug: Log the data being sent
      console.log('Sending data to API:', JSON.stringify(relationsToSave, null, 2));
      console.log('Original doRelations:', JSON.stringify(doRelations, null, 2));

      await saveRelations(relationsToSave);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save module preferences:', error);
      // TODO: Show error message to user
    }
  };


  // Handle Add Group button click
  const handleAddGroup = () => {
    setAddGroupModalOpen(true);
    setNewGroupName('');
  };

  // Handle add group confirmation
  const handleConfirmAddGroup = async () => {
    if (!newGroupName.trim()) return;

    setIsAddingGroup(true);
    try {
      await axios.post('/userPreference/rg/add/rg', {
        rgName: newGroupName.trim()
      });

      // Always refresh the list from the backend to get the latest data
      const groupsResponse = await axios.get('/rg/rgs-for-operator');
      setIpcAccountingGroups(groupsResponse.data);

      // Close modal and reset state
      setAddGroupModalOpen(false);
      setNewGroupName('');
    } catch (error) {
      console.error('Failed to add group:', error);
      // TODO: Show error message to user
    } finally {
      setIsAddingGroup(false);
    }
  };

  // Handle add group cancellation
  const handleCancelAddGroup = () => {
    setAddGroupModalOpen(false);
    setNewGroupName('');
  };

  // Handle Edit Group button click
  const handleEditGroup = (group: IPCAccountingGroup) => {
    setEditingGroupId(group.id);
    setEditingGroupName(group.name);
  };

  // Handle Delete Group button click
  const handleDeleteGroup = (group: IPCAccountingGroup) => {
    setGroupToDelete(group);
    setDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (!groupToDelete) return;

    try {
      await axios.delete(`/userPreference/rg/remove/rg?rgId=${groupToDelete.id}`);

      // Always refresh the list from the backend to get the latest data
      const groupsResponse = await axios.get('/rg/rgs-for-operator');
      setIpcAccountingGroups(groupsResponse.data);

      // Reset editing state if we were editing this group
      if (editingGroupId === groupToDelete.id) {
        setEditingGroupId(null);
        setEditingGroupName('');
      }

      // Close modal and reset state
      setDeleteModalOpen(false);
      setGroupToDelete(null);
    } catch (error) {
      console.error('Failed to delete group:', error);
      // TODO: Show error message to user
    }
  };

  // Handle delete cancellation
  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setGroupToDelete(null);
  };

  // Handle Save Edit
  const handleSaveEdit = async () => {
    if (!editingGroupId || !editingGroupName.trim()) return;

    setIsSavingEdit(true);
    try {
      await axios.post('/userPreference/rg/edit/rg', {
        rgId: editingGroupId,
        rgName: editingGroupName.trim()
      });

      // Always refresh the list from the backend to get the latest data
      const groupsResponse = await axios.get('/rg/rgs-for-operator');
      setIpcAccountingGroups(groupsResponse.data);

      // Reset editing state
      setEditingGroupId(null);
      setEditingGroupName('');
    } catch (error) {
      console.error('Failed to save group name:', error);
      // TODO: Show error message to user
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Handle Cancel Edit
  const handleCancelEdit = () => {
    setEditingGroupId(null);
    setEditingGroupName('');
  };

  // UPU Group Handlers

  // Handle Add UPU Group button click
  const handleAddUpuGroup = () => {
    setAddUpuGroupModalOpen(true);
    setNewUpuGroupName('');
  };

  // Handle add UPU group confirmation
  const handleConfirmAddUpuGroup = async () => {
    if (!newUpuGroupName.trim()) return;

    setIsAddingUpuGroup(true);
    try {
      await axios.post('/upu-rg/add', {
        rgName: newUpuGroupName.trim()
      });

      // Always refresh the list from the backend to get the latest data
      const groupsResponse = await axios.get('/upu-rg/rgs-for-operator');
      setUpuAccountingGroups(groupsResponse.data);

      // Close modal and reset state
      setAddUpuGroupModalOpen(false);
      setNewUpuGroupName('');
    } catch (error) {
      console.error('Failed to add UPU group:', error);
      // TODO: Show error message to user
    } finally {
      setIsAddingUpuGroup(false);
    }
  };

  // Handle add UPU group cancellation
  const handleCancelAddUpuGroup = () => {
    setAddUpuGroupModalOpen(false);
    setNewUpuGroupName('');
  };

  // Handle Edit UPU Group button click
  const handleEditUpuGroup = (group: UPUAccountingGroup) => {
    setEditingUpuGroupId(group.id);
    setEditingUpuGroupName(group.name);
  };

  // Handle Delete UPU Group button click
  const handleDeleteUpuGroup = (group: UPUAccountingGroup) => {
    setUpuGroupToDelete(group);
    setDeleteUpuModalOpen(true);
  };

  // Handle UPU delete confirmation
  const handleConfirmDeleteUpu = async () => {
    if (!upuGroupToDelete) return;

    try {
      await axios.delete(`/upu-rg/${upuGroupToDelete.id}`);

      // Always refresh the list from the backend to get the latest data
      const groupsResponse = await axios.get('/upu-rg/rgs-for-operator');
      setUpuAccountingGroups(groupsResponse.data);

      // Reset editing state if we were editing this group
      if (editingUpuGroupId === upuGroupToDelete.id) {
        setEditingUpuGroupId(null);
        setEditingUpuGroupName('');
      }

      // Close modal and reset state
      setDeleteUpuModalOpen(false);
      setUpuGroupToDelete(null);
    } catch (error) {
      console.error('Failed to delete UPU group:', error);
      // TODO: Show error message to user
    }
  };

  // Handle UPU delete cancellation
  const handleCancelDeleteUpu = () => {
    setDeleteUpuModalOpen(false);
    setUpuGroupToDelete(null);
  };

  // Handle Save UPU Edit
  const handleSaveUpuEdit = async () => {
    if (!editingUpuGroupId || !editingUpuGroupName.trim()) return;

    setIsSavingUpuEdit(true);
    try {
      await axios.put('/upu-rg/rename', {
        rgId: editingUpuGroupId,
        rgName: editingUpuGroupName.trim()
      });

      // Always refresh the list from the backend to get the latest data
      const groupsResponse = await axios.get('/upu-rg/rgs-for-operator');
      setUpuAccountingGroups(groupsResponse.data);

      // Reset editing state
      setEditingUpuGroupId(null);
      setEditingUpuGroupName('');
    } catch (error) {
      console.error('Failed to save UPU group name:', error);
      // TODO: Show error message to user
    } finally {
      setIsSavingUpuEdit(false);
    }
  };

  // Handle Cancel UPU Edit
  const handleCancelUpuEdit = () => {
    setEditingUpuGroupId(null);
    setEditingUpuGroupName('');
  };

  // Handle Operator Module checkbox change
  const handleOperatorModuleCheckboxChange = (operatorId: string, moduleName: string) => {
    setOperatorModules(prevModules =>
      prevModules.map(module =>
        module.operatorId === operatorId
          ? {
            ...module,
            modules: {
              ...module.modules,
              [moduleName]: !module.modules[moduleName]
            }
          }
          : module
      )
    );
    setHasUnsavedOperatorChanges(true);
  };

  // Handle Save Operator Modules
  const handleSaveOperatorModules = async () => {
    if (!isAdmin) return;

    try {
      // Collect all modules (both enabled and disabled) that need to be saved
      const allChanges: Array<{ operatorId: string; moduleName: string; enabled: boolean }> = [];

      operatorModules.forEach(operatorModule => {
        operatorModuleColumns.forEach(column => {
          const currentEnabled = operatorModule.modules[column.id] || false;
          // Map display name back to backend module name
          const backendModuleName = operatorModuleColumnMapping[column.id];
          if (backendModuleName) {
            // Include all modules (both enabled and disabled) to ensure false values are sent
            allChanges.push({
              operatorId: operatorModule.operatorId,
              moduleName: backendModuleName,
              enabled: currentEnabled
            });
          }
        });
      });

      // Send all modules in a single PUT request (bulk update)
      if (allChanges.length > 0) {
        await axios.put('/operator-modules/bulk', allChanges);
      }

      setHasUnsavedOperatorChanges(false);

      // Refresh data from backend
      let allModules: OperatorModule[] = [];
      let currentPage = 0;
      let totalPages = 1;

      do {
        const response = await axios.get<PaginatedOperatorModulesResponse>('/operator-modules', {
          params: {
            page: currentPage,
            size: 100
          }
        });

        allModules = [...allModules, ...response.data.content];
        totalPages = response.data.totalPages;
        currentPage++;
      } while (currentPage < totalPages);

      // Get unique operator IDs from modules only
      const operatorIdsFromModules = [...new Set(allModules.map(m => m.operatorId))];
      const allOperatorIds = operatorIdsFromModules;

      const grouped: Record<string, OperatorModulesData> = {};

      // Initialize all operators
      allOperatorIds.forEach(operatorId => {
        grouped[operatorId] = {
          operatorId,
          modules: {}
        };
      });

      // Populate modules from refreshed data, mapping backend names to display names
      allModules.forEach((item) => {
        if (!grouped[item.operatorId]) {
          grouped[item.operatorId] = {
            operatorId: item.operatorId,
            modules: {}
          };
        }
        // Map backend module name to display name
        const displayName = mapBackendToDisplayName(item.moduleName);
        if (displayName) {
          grouped[item.operatorId].modules[displayName] = item.enabled;
        }
      });

      // Initialize missing modules as false for all operators (using fixed columns)
      fixedOperatorModuleColumns.forEach(column => {
        Object.keys(grouped).forEach(operatorId => {
          if (!grouped[operatorId].modules.hasOwnProperty(column.id)) {
            grouped[operatorId].modules[column.id] = false;
          }
        });
      });

      const sortedOperators = Object.values(grouped).sort((a, b) =>
        a.operatorId.localeCompare(b.operatorId)
      );

      setOperatorModules(sortedOperators);

      // Show success notification
      setOperatorModulesNotification({
        open: true,
        message: 'Operator modules saved successfully',
        severity: 'success'
      });
    } catch (error: any) {
      console.error('Failed to save operator modules:', error);
      // Show error notification
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save operator modules. Please try again.';
      setOperatorModulesNotification({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  // Handle operator modules notification close
  const handleOperatorModulesNotificationClose = () => {
    setOperatorModulesNotification(prev => ({ ...prev, open: false }));
  };


  return (
    <Paper elevation={0} sx={{ p: 2 }} data-testid="module-preferences-container">
      <Box sx={{ width: '100%', mb: 3 }}>
        <CustomTabs>
          <CustomTab
            icon={<ViewModule />}
            label="Standard Modules"
            isActive={activeTab === 0}
            onClick={() => setActiveTab(0)}
          />
          <CustomTab
            icon={<AccountBalance />}
            label="IPC Accounting"
            isActive={activeTab === 1}
            onClick={() => setActiveTab(1)}
          />
          <CustomTab
            icon={<CorporateFare />}
            label="UPU Accounting"
            isActive={activeTab === 2}
            onClick={() => setActiveTab(2)}
          />
          {isAdmin && (
            <CustomTab
              icon={<AdminPanelSettings />}
              label="Operator Modules"
              isActive={activeTab === 3}
              onClick={() => setActiveTab(3)}
            />
          )}
        </CustomTabs>

        {activeTab === 0 && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{
              mb: 5,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'top',
              maxWidth: '770px'
            }}>
              <Box>
                <Box sx={{ mb: 3, fontWeight: 'bold' }}>
                  Filtering Accounting Year
                </Box>
                <FormControl variant="outlined" sx={{ width: 200 }}>
                  <Select
                    id="year-select"
                    value={selectedYear}
                    onChange={handleYearChange}
                    data-testid="year-select"
                    size="small"
                    disabled={isLoadingYears || yearsError !== null}
                  >
                    {isLoadingYears ? (
                      <MenuItem disabled>
                        Loading years...
                      </MenuItem>
                    ) : yearsError ? (
                      <MenuItem disabled>
                        Error loading years
                      </MenuItem>
                    ) : (
                      availableYears.map(year => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Box>
              <Tooltip title={hasModuleAccess ? "" : "You don't have permission to save preferences"}>
                <span>
                  <SaveButton
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={!hasModuleAccess || isLoadingRelations || isLoadingYears || moduleColumns.length === 0 || !selectedYear}
                    data-testid="save-preferences-button"
                  >
                    {isLoadingRelations ? 'Saving...' : isLoadingYears ? 'Loading...' : t('profile.modulePreferences.savePreferences', `SAVE ${selectedYear} PREFERENCES`)}
                  </SaveButton>
                </span>
              </Tooltip>
            </Box>

            <Box sx={{ mb: 4 }} maxWidth="770px">
              {isLoadingYears ? (
                <Box sx={{
                  textAlign: 'center',
                  py: 4,
                  color: 'text.secondary',
                  border: '1px solid #E0E0E0',
                  borderRadius: 1,
                  backgroundColor: '#FAFAFA'
                }}>
                  Loading available years...
                </Box>
              ) : yearsError ? (
                <Box sx={{
                  textAlign: 'center',
                  py: 4,
                  color: 'error.main',
                  border: '1px solid #E0E0E0',
                  borderRadius: 1,
                  backgroundColor: '#FAFAFA'
                }}>
                  {yearsError}
                </Box>
              ) : moduleColumns.length === 0 ? (
                <Box sx={{
                  textAlign: 'center',
                  py: 4,
                  color: 'text.secondary',
                  border: '1px solid #E0E0E0',
                  borderRadius: 1,
                  backgroundColor: '#FAFAFA'
                }}>
                  {isLoadingRelations ? 'Loading module preferences...' : 'No module preferences available for this year'}
                </Box>
              ) : (
                <Box>
                  <TableContainer>
                    <Table size="small" data-testid="do-module-table">
                      <TableHead>
                        <TableRow>
                          <HeaderCell sx={{ backgroundColor: 'white', minWidth: '100px', borderBottom: 'none', }}></HeaderCell>
                          {moduleColumns.map((column, index) => {
                            // For columns with headers, start with white and alternate with #E7EFF5
                            // First column (index 0) is white, second (index 1) is #E7EFF5, etc.
                            const bgColor = index % 2 === 0 ? 'white' : '#E7EFF5';

                            return (
                              <HeaderCell
                                key={column.id}
                                align="center"
                                sx={{
                                  backgroundColor: bgColor,
                                  fontSize: '14px',
                                  whiteSpace: 'nowrap',
                                  minWidth: '80px',
                                  borderTopLeftRadius: '12px',
                                  borderTopRightRadius: '12px',
                                  borderBottom: 'none',
                                  px: 1
                                }}
                              >
                                {column.name}
                              </HeaderCell>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {isLoadingRelations ? (
                          <StyledTableRow>
                            <StyledTableCell
                              colSpan={moduleColumns.length + 1}
                              columnIndex={0}
                              sx={{
                                textAlign: 'center',
                                py: 3,
                                borderBottom: 'none'
                              }}
                            >
                              Loading module preferences...
                            </StyledTableCell>
                          </StyledTableRow>
                        ) : localDORelations.length === 0 ? (
                          <StyledTableRow>
                            <StyledTableCell
                              colSpan={moduleColumns.length + 1}
                              columnIndex={0}
                              sx={{
                                textAlign: 'center',
                                py: 3,
                                color: 'text.secondary',
                                borderBottom: 'none'
                              }}
                            >
                              No designated operator relations available
                            </StyledTableCell>
                          </StyledTableRow>
                        ) : (
                          localDORelations.map(relation => {
                            const showAsterisk = hasNonEditableModule(relation);
                            return (
                              <StyledTableRow key={relation.id} data-testid={`do-relation-row-${relation.id}`}>
                                <StyledTableCell component="th" scope="row" columnIndex={0}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {relation.targetDO}
                                    {showAsterisk && (
                                      <Box component="span" sx={{ color: 'error.main', fontWeight: 'bold' }}>*</Box>
                                    )}
                                  </Box>
                                </StyledTableCell>
                                {moduleColumns.map((column, index) => {
                                  // For columns with content, start with white and alternate with #E7EFF5
                                  const columnType = index % 2 === 0 ? 0 : 1; // 0 for white, 1 for #E7EFF5
                                  const isDisabled = isModuleCheckboxDisabled(relation, column.id);
                                  const isNotEditable = relation.moduleEditability[column.id] === false;
                                  const tooltipMessage = isNotEditable
                                    ? "This designated operator relation is not editable"
                                    : isDisabled
                                      ? "You don't have access to this module"
                                      : "";

                                  return (
                                    <StyledTableCell
                                      key={`${relation.id}-${column.id}`}
                                      align="center"
                                      padding="checkbox"
                                      columnIndex={columnType}
                                      sx={{
                                        minWidth: '80px',
                                        px: 1
                                      }}
                                    >
                                      <Tooltip title={tooltipMessage}>
                                        <span>
                                          <StyledCheckbox
                                            checked={relation.modules[column.id] || false}
                                            onChange={() => handleDORelationCheckboxChange(relation.targetDO, column.id)}
                                            size="small"
                                            disabled={isDisabled}
                                            data-testid={`do-checkbox-${relation.targetDO}-${column.id}`}
                                          />
                                        </span>
                                      </Tooltip>
                                    </StyledTableCell>
                                  );
                                })}
                              </StyledTableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {localDORelations.some(relation => hasNonEditableModule(relation)) && (
                    <FootnoteContainer sx={{ mt: 2, maxWidth: '770px' }}>
                      <FootnoteText>
                        <Box component="span" sx={{ color: 'error.main', fontWeight: 'bold' }}>*</Box>
                         {" "}eNO1 Module is disabled and cannot be enabled for designated operators.
                      </FootnoteText>
                    </FootnoteContainer>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ mb: 4 }} maxWidth="300px">
              <TableContainer sx={{ border: '1px solid #E0E0E0', borderRadius: 1 }}>
                <Table size="small" data-testid="ipc-accounting-table">
                  <TableHead>
                    <TableRow>
                      <HeaderCell
                        sx={{
                          backgroundColor: '#F5F5F5',
                          minWidth: '200px',
                          width: '200px',
                          borderBottom: '1px solid #E0E0E0',
                          borderRight: '1px solid #E0E0E0',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          borderTopLeftRadius: '4px'
                        }}
                      >
                        Name of Group
                      </HeaderCell>
                      <HeaderCell
                        sx={{
                          backgroundColor: '#F5F5F5',
                          minWidth: '50px',
                          width: '50px',
                          borderBottom: '1px solid #E0E0E0',
                          borderTopRightRadius: '4px'
                        }}
                      >
                      </HeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoadingGroups ? (
                      <StyledTableRow>
                        <StyledTableCell
                          colSpan={2}
                          columnIndex={0}
                          sx={{
                            textAlign: 'center',
                            py: 3,
                            borderBottom: 'none'
                          }}
                        >
                          Loading groups...
                        </StyledTableCell>
                      </StyledTableRow>
                    ) : ipcAccountingGroups.length === 0 ? (
                      <StyledTableRow>
                        <StyledTableCell
                          colSpan={2}
                          columnIndex={0}
                          sx={{
                            textAlign: 'center',
                            py: 3,
                            color: 'text.secondary',
                            borderBottom: 'none'
                          }}
                        >
                          No groups available
                        </StyledTableCell>
                      </StyledTableRow>
                    ) : (
                      ipcAccountingGroups.map((group, index) => (
                        <StyledTableRow key={group.id} data-testid={`ipc-group-row-${group.id}`}>
                          <StyledTableCell
                            component="th"
                            scope="row"
                            columnIndex={0}
                            sx={{
                              width: '200px',
                              borderRight: '1px solid #E0E0E0',
                              borderBottom: index === ipcAccountingGroups.length - 1 ? 'none' : '1px solid #E0E0E0',
                              padding: '4px'
                            }}
                          >
                            {editingGroupId === group.id ? (
                              <TextField
                                value={editingGroupName}
                                onChange={(e) => setEditingGroupName(e.target.value)}
                                variant="outlined"
                                size="small"
                                fullWidth
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveEdit();
                                  } else if (e.key === 'Escape') {
                                    handleCancelEdit();
                                  }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    fontSize: '14px',
                                    height: '32px'
                                  }
                                }}
                                data-testid={`edit-input-${group.id}`}
                              />
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  height: '32px',
                                  padding: '8px 12px',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '4px',
                                  backgroundColor: '#fafafa',
                                  fontSize: '14px',
                                  fontWeight: 400,
                                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                  color: 'rgba(0, 0, 0, 0.87)',
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  lineHeight: 1.4375
                                }}
                                data-testid={`display-name-${group.id}`}
                              >
                                {group.displayName}
                              </Box>
                            )}
                          </StyledTableCell>
                          <StyledTableCell
                            columnIndex={0}
                            sx={{
                              width: '50px',
                              textAlign: 'center',
                              borderBottom: index === ipcAccountingGroups.length - 1 ? 'none' : '1px solid #E0E0E0',
                              padding: '4px'
                            }}
                          >
                            {/* Show edit/delete or save/cancel icons based on editing state */}
                            {!group.predefined ? (
                              editingGroupId === group.id ? (
                                /* Save/Cancel buttons when editing */
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                  <Tooltip title="Save">
                                    <IconButton
                                      size="small"
                                      onClick={handleSaveEdit}
                                      disabled={isSavingEdit || !editingGroupName.trim()}
                                      sx={{
                                        padding: '2px',
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                      }}
                                      data-testid={`save-group-${group.id}`}
                                    >
                                      <Check sx={{ fontSize: '16px', color: '#4caf50' }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Cancel">
                                    <IconButton
                                      size="small"
                                      onClick={handleCancelEdit}
                                      disabled={isSavingEdit}
                                      sx={{
                                        padding: '2px',
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                      }}
                                      data-testid={`cancel-group-${group.id}`}
                                    >
                                      <Close sx={{ fontSize: '16px', color: '#f44336' }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                /* Edit/Delete buttons when not editing */
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                  <Tooltip title="Edit Group">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditGroup(group)}
                                      sx={{
                                        padding: '2px',
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                      }}
                                      data-testid={`edit-group-${group.id}`}
                                    >
                                      <Edit sx={{ fontSize: '16px', color: '#1976d2' }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete Group">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteGroup(group)}
                                      sx={{
                                        padding: '2px',
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                      }}
                                      data-testid={`delete-group-${group.id}`}
                                    >
                                      <Delete sx={{ fontSize: '16px', color: '#d32f2f' }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )
                            ) : null}
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Add Group Button */}
            <Box sx={{ mt: 2, maxWidth: '220px' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddGroup}
                sx={{
                  height: '36px',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  fontSize: '14px'
                }}
                data-testid="add-group-button"
              >
                Add Group
              </Button>
            </Box>
          </Box>
        )}

        {activeTab === 2 && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{ mb: 4 }} maxWidth="300px">
              <TableContainer sx={{ border: '1px solid #E0E0E0', borderRadius: 1 }}>
                <Table size="small" data-testid="upu-accounting-table">
                  <TableHead>
                    <TableRow>
                      <HeaderCell
                        sx={{
                          backgroundColor: '#F5F5F5',
                          minWidth: '200px',
                          width: '200px',
                          borderBottom: '1px solid #E0E0E0',
                          borderRight: '1px solid #E0E0E0',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          borderTopLeftRadius: '4px'
                        }}
                      >
                        Name of Group
                      </HeaderCell>
                      <HeaderCell
                        sx={{
                          backgroundColor: '#F5F5F5',
                          minWidth: '50px',
                          width: '50px',
                          borderBottom: '1px solid #E0E0E0',
                          borderTopRightRadius: '4px'
                        }}
                      >
                      </HeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoadingUpuGroups ? (
                      <StyledTableRow>
                        <StyledTableCell
                          colSpan={2}
                          columnIndex={0}
                          sx={{
                            textAlign: 'center',
                            py: 3,
                            borderBottom: 'none'
                          }}
                        >
                          Loading groups...
                        </StyledTableCell>
                      </StyledTableRow>
                    ) : upuAccountingGroups.length === 0 ? (
                      <StyledTableRow>
                        <StyledTableCell
                          colSpan={2}
                          columnIndex={0}
                          sx={{
                            textAlign: 'center',
                            py: 3,
                            color: 'text.secondary',
                            borderBottom: 'none'
                          }}
                        >
                          No groups available
                        </StyledTableCell>
                      </StyledTableRow>
                    ) : (
                      upuAccountingGroups.map((group, index) => (
                        <StyledTableRow key={group.id} data-testid={`upu-group-row-${group.id}`}>
                          <StyledTableCell
                            component="th"
                            scope="row"
                            columnIndex={0}
                            sx={{
                              width: '200px',
                              borderRight: '1px solid #E0E0E0',
                              borderBottom: index === upuAccountingGroups.length - 1 ? 'none' : '1px solid #E0E0E0',
                              padding: '4px'
                            }}
                          >
                            {editingUpuGroupId === group.id ? (
                              <TextField
                                value={editingUpuGroupName}
                                onChange={(e) => setEditingUpuGroupName(e.target.value)}
                                variant="outlined"
                                size="small"
                                fullWidth
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveUpuEdit();
                                  } else if (e.key === 'Escape') {
                                    handleCancelUpuEdit();
                                  }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    fontSize: '14px',
                                    height: '32px'
                                  }
                                }}
                                data-testid={`edit-upu-input-${group.id}`}
                              />
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  height: '32px',
                                  padding: '8px 12px',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '4px',
                                  backgroundColor: '#fafafa',
                                  fontSize: '14px',
                                  fontWeight: 400,
                                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                  color: 'rgba(0, 0, 0, 0.87)',
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  lineHeight: 1.4375
                                }}
                                data-testid={`display-upu-name-${group.id}`}
                              >
                                {group.displayName}
                              </Box>
                            )}
                          </StyledTableCell>
                          <StyledTableCell
                            columnIndex={0}
                            sx={{
                              width: '50px',
                              textAlign: 'center',
                              borderBottom: index === upuAccountingGroups.length - 1 ? 'none' : '1px solid #E0E0E0',
                              padding: '4px'
                            }}
                          >
                            {/* Show edit/delete or save/cancel icons based on editing state */}
                            {!group.predefined && group.operatorId !== null ? (
                              editingUpuGroupId === group.id ? (
                                /* Save/Cancel buttons when editing */
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                  <Tooltip title="Save">
                                    <IconButton
                                      size="small"
                                      onClick={handleSaveUpuEdit}
                                      disabled={isSavingUpuEdit || !editingUpuGroupName.trim()}
                                      sx={{
                                        padding: '2px',
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                      }}
                                      data-testid={`save-upu-group-${group.id}`}
                                    >
                                      <Check sx={{ fontSize: '16px', color: '#4caf50' }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Cancel">
                                    <IconButton
                                      size="small"
                                      onClick={handleCancelUpuEdit}
                                      disabled={isSavingUpuEdit}
                                      sx={{
                                        padding: '2px',
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                      }}
                                      data-testid={`cancel-upu-group-${group.id}`}
                                    >
                                      <Close sx={{ fontSize: '16px', color: '#f44336' }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              ) : (
                                /* Edit/Delete buttons when not editing */
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                  <Tooltip title="Edit Group">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditUpuGroup(group)}
                                      sx={{
                                        padding: '2px',
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                      }}
                                      data-testid={`edit-upu-group-${group.id}`}
                                    >
                                      <Edit sx={{ fontSize: '16px', color: '#1976d2' }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete Group">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteUpuGroup(group)}
                                      sx={{
                                        padding: '2px',
                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                      }}
                                      data-testid={`delete-upu-group-${group.id}`}
                                    >
                                      <Delete sx={{ fontSize: '16px', color: '#d32f2f' }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              )
                            ) : null}
                          </StyledTableCell>
                        </StyledTableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Add UPU Group Button */}
            <Box sx={{ mt: 2, maxWidth: '220px' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddUpuGroup}
                sx={{
                  height: '36px',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  fontSize: '14px'
                }}
                data-testid="add-upu-group-button"
              >
                Add Group
              </Button>
            </Box>
          </Box>
        )}

        {activeTab === 3 && isAdmin && (
          <Box sx={{ mt: 3 }}>
            <Box sx={{
              mb: 5,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'top',
              maxWidth: '770px'
            }}>
              <Tooltip title="">
                <span>
                  <SaveButton
                    variant="contained"
                    color="primary"
                    onClick={handleSaveOperatorModules}
                    disabled={isLoadingOperatorModules || operatorModules.length === 0 || !hasUnsavedOperatorChanges}
                    data-testid="save-operator-modules-button"
                  >
                    {isLoadingOperatorModules ? 'Loading...' : 'SAVE OPERATOR MODULES'}
                  </SaveButton>
                </span>
              </Tooltip>
            </Box>

            <Box sx={{ mb: 4 }} maxWidth="770px">
              {isLoadingOperatorModules ? (
                <Box sx={{
                  textAlign: 'center',
                  py: 4,
                  color: 'text.secondary',
                  border: '1px solid #E0E0E0',
                  borderRadius: 1,
                  backgroundColor: '#FAFAFA'
                }}>
                  Loading operator modules...
                </Box>
              ) : operatorModules.length === 0 ? (
                <Box sx={{
                  textAlign: 'center',
                  py: 4,
                  color: 'text.secondary',
                  border: '1px solid #E0E0E0',
                  borderRadius: 1,
                  backgroundColor: '#FAFAFA'
                }}>
                  No operator modules available
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small" data-testid="operator-modules-table">
                    <TableHead>
                      <TableRow>
                        <HeaderCell sx={{ backgroundColor: 'white', minWidth: '100px', borderBottom: 'none' }}></HeaderCell>
                        {operatorModuleColumns.map((column, index) => {
                          const bgColor = index % 2 === 0 ? 'white' : '#E7EFF5';

                          return (
                            <HeaderCell
                              key={column.id}
                              align="center"
                              sx={{
                                backgroundColor: bgColor,
                                fontSize: '14px',
                                whiteSpace: 'nowrap',
                                minWidth: '80px',
                                borderTopLeftRadius: '12px',
                                borderTopRightRadius: '12px',
                                borderBottom: 'none',
                                px: 1
                              }}
                            >
                              {column.name}
                            </HeaderCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {operatorModules.map(operatorModule => (
                        <StyledTableRow key={operatorModule.operatorId} data-testid={`operator-module-row-${operatorModule.operatorId}`}>
                          <StyledTableCell component="th" scope="row" columnIndex={0}>
                            {operatorModule.operatorId}
                          </StyledTableCell>
                          {operatorModuleColumns.map((column, index) => {
                            const columnType = index % 2 === 0 ? 0 : 1;

                            return (
                              <StyledTableCell
                                key={`${operatorModule.operatorId}-${column.id}`}
                                align="center"
                                padding="checkbox"
                                columnIndex={columnType}
                                sx={{
                                  minWidth: '80px',
                                  px: 1
                                }}
                              >
                                <StyledCheckbox
                                  checked={operatorModule.modules[column.id] || false}
                                  onChange={() => handleOperatorModuleCheckboxChange(operatorModule.operatorId, column.id)}
                                  size="small"
                                  data-testid={`operator-checkbox-${operatorModule.operatorId}-${column.id}`}
                                />
                              </StyledTableCell>
                            );
                          })}
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Box>
        )}
      </Box>
      {/* Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Unsaved Changes"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You have unsaved changes for the year {selectedYear}. Would you like to discard these changes and switch to {pendingYear}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmYearChange} color="primary" autoFocus>
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {"Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the group "{groupToDelete?.displayName}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Group Dialog */}
      <Dialog
        open={addGroupModalOpen}
        onClose={handleCancelAddGroup}
        aria-labelledby="add-group-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="add-group-dialog-title">
          {"Add New Group"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Group Name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              variant="outlined"
              fullWidth
              autoFocus
              disabled={isAddingGroup}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newGroupName.trim()) {
                  handleConfirmAddGroup();
                } else if (e.key === 'Escape') {
                  handleCancelAddGroup();
                }
              }}
              placeholder="Enter group name"
              data-testid="new-group-name-input"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelAddGroup}
            color="primary"
            disabled={isAddingGroup}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAddGroup}
            color="primary"
            variant="contained"
            disabled={isAddingGroup || !newGroupName.trim()}
            autoFocus
          >
            {isAddingGroup ? 'Adding...' : 'Add Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* UPU Delete Confirmation Dialog */}
      <Dialog
        open={deleteUpuModalOpen}
        onClose={handleCancelDeleteUpu}
        aria-labelledby="delete-upu-dialog-title"
        aria-describedby="delete-upu-dialog-description"
      >
        <DialogTitle id="delete-upu-dialog-title">
          {"Confirm Delete"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-upu-dialog-description">
            Are you sure you want to delete the UPU group "{upuGroupToDelete?.displayName}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteUpu} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDeleteUpu} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add UPU Group Dialog */}
      <Dialog
        open={addUpuGroupModalOpen}
        onClose={handleCancelAddUpuGroup}
        aria-labelledby="add-upu-group-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="add-upu-group-dialog-title">
          {"Add New UPU Group"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Group Name"
              value={newUpuGroupName}
              onChange={(e) => setNewUpuGroupName(e.target.value)}
              variant="outlined"
              fullWidth
              autoFocus
              disabled={isAddingUpuGroup}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newUpuGroupName.trim()) {
                  handleConfirmAddUpuGroup();
                } else if (e.key === 'Escape') {
                  handleCancelAddUpuGroup();
                }
              }}
              placeholder="Enter group name"
              data-testid="new-upu-group-name-input"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelAddUpuGroup}
            color="primary"
            disabled={isAddingUpuGroup}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAddUpuGroup}
            color="primary"
            variant="contained"
            disabled={isAddingUpuGroup || !newUpuGroupName.trim()}
            autoFocus
          >
            {isAddingUpuGroup ? 'Adding...' : 'Add Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Operator Modules Notification */}
      <NotificationSnackbar
        open={operatorModulesNotification.open}
        message={operatorModulesNotification.message}
        severity={operatorModulesNotification.severity}
        onClose={handleOperatorModulesNotificationClose}
        data-testid="operator-modules-notification"
      />
    </Paper>
  );
};

export default ModulePreferences; 